import { CAPTURE_FPS } from '~/lib/constants'

/** Run order within a tick. `playhead` seeks a file before `upload` reads it; `upload` precedes `tween` so a fade samples fresh textures; `automation` follows `tween` so a loop's sample wins over a fade's write; `render` sees both. */
export const STAGES = ['tempo', 'playhead', 'upload', 'tween', 'automation', 'render']

const PERIOD = 1000 / CAPTURE_FPS
// A quarter period of slack absorbs timer jitter without admitting a second frame.
const MIN_GAP = PERIOD * 0.75

const logger = console.scope?.('clock') ?? console

export function createClock({ createWorker, now = () => performance.now() } = {}) {
	const subs = new Map(STAGES.map(s => [s, new Set()]))
	let worker = null
	let lastAt = -Infinity
	let busy = false

	const count = () => {
		let n = 0
		for (const set of subs.values()) n += set.size
		return n
	}

	const tick = async () => {
		const t = now()
		// ⚠️ COALESCE A BACKLOG: a busy main thread queues ticks that would drain back to back — a stutter, not a catch-up. A tick still awaiting its upload stage drops the next one for the same reason.
		if (busy || t - lastAt < MIN_GAP) return
		lastAt = t
		busy = true
		try {
			for (const stage of STAGES) {
				for (const fn of subs.get(stage)) {
					try {
						const r = fn(t)
						if (r?.then) await r
					} catch (e) {
						logger.error(`tick ${stage}:`, e)
					}
				}
			}
		} finally {
			busy = false
		}
	}

	const start = () => {
		worker = createWorker()
		worker.onmessage = tick
		worker.postMessage({ type: 'start', fps: CAPTURE_FPS })
	}

	const stop = () => {
		worker.terminate()
		worker = null
		lastAt = -Infinity
	}

	return {
		/** Subscribe `fn(now)` to a stage; returns the unsubscribe. The worker runs while anything is subscribed. */
		onTick(stage, fn) {
			const set = subs.get(stage)
			if (!set) throw new Error(`unknown clock stage: ${stage}`)
			set.add(fn)
			if (!worker) start()
			return () => {
				if (!set.delete(fn)) return
				if (worker && !count()) stop()
			}
		},
		running: () => !!worker,
		now,
	}
}

const clock = createClock({
	createWorker: () => new Worker(new URL('../frameClockWorker.js', import.meta.url)),
})

export const { onTick } = clock
export default clock
